import { useState } from 'react'
import { useProducts, useProductCategories, useDeleteProduct } from '@hooks/useProcurement'
import { ProductForm } from '@components/procurement/ProductForm'

export function ProductListPage() {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [productType, setProductType] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useProducts(search, productType)
  const { data: categoriesData } = useProductCategories()
  const delete_ = useDeleteProduct()

  const products = data?.data?.items || []
  const total = data?.data?.total || 0
  const pageSize = data?.data?.pageSize || 20
  const totalPages = data?.data?.totalPages || 0
  const categories = categoriesData?.data || []

  const filteredProducts = category
    ? products.filter((p) => p.category === category)
    : products

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      delete_.mutate(id)
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingId(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600 text-sm mt-1">Manage your product master</p>
        </div>
        <button
          onClick={() => {
            setEditingId(null)
            setShowForm(!showForm)
          }}
          className="btn-primary"
        >
          + Add Product
        </button>
      </div>

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              {editingId ? 'Edit Product' : 'New Product'}
            </h2>
          </div>
          <ProductForm
            initialData={
              editingId
                ? products.find((p) => p.id === editingId)
                : null
            }
            onSuccess={handleFormClose}
            onCancel={handleFormClose}
          />
        </div>
      )}

      <div className="card">
        <div className="space-y-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              type="text"
              placeholder="Search by name or HSN..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full"
            />

            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value)
                setPage(1)
              }}
              className="w-full"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <select
              value={productType}
              onChange={(e) => {
                setProductType(e.target.value)
                setPage(1)
              }}
              className="w-full"
            >
              <option value="">All Types</option>
              <option value="REPACK">Repack</option>
              <option value="DIRECT_SALE">Direct Sale</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            Failed to load products. Please try again.
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No products found matching your search.' : 'No products yet. Create one to get started.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Name
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Category
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      HSN
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      UOM
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Type
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Shelf Life
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-medium">{product.name}</td>
                      <td className="px-4 py-3 text-sm">
                        {product.category ? (
                          <span className="badge badge-success">{product.category}</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{product.hsnCode || '-'}</td>
                      <td className="px-4 py-3 text-sm font-mono">
                        {product.baseUom}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`badge ${
                            product.productType === 'REPACK'
                              ? 'badge-success'
                              : 'badge-warning'
                          }`}
                        >
                          {product.productType === 'REPACK' ? 'Repack' : 'Direct Sale'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {product.shelfLifeDays ? `${product.shelfLifeDays}d` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingId(product.id)
                              setShowForm(true)
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            disabled={delete_.isPending}
                            className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
